import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  finalize,
  map,
  of,
  switchMap,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserProfileData } from 'src/app/interfaces/user-profile-data';
import { BackendAccessService } from '../../Services/APICalls/backend-access-service';
import { AuthService } from '../../Services/Auth/auth.service';
import { LoadingService } from '../../Services/Loading/loading-service.service';

@Injectable({
  providedIn: 'root',
})
export class CanActivateService {
  constructor(
    private userAuth: AuthService,
    private backend: BackendAccessService,
    private loadingService: LoadingService,
    private router: Router // private productService: ProductService
  ) {}
  roles: string[] = [];
  roleValue: number = -1;
  loading: boolean = false;
  multipleBranches$ = new ReplaySubject<boolean>(1);
  branchAssignments$ = new BehaviorSubject<any[]>([]);
  companyProfile$ = new BehaviorSubject<any | undefined>(undefined);
  isOnboardingUser: boolean = false;
  branchAssignmentsDetails$ = this.branchAssignments$.asObservable().pipe(
    switchMap((branches: unknown[]) => {
      return this.backend.getBranchDetails(branches);
    })
  );

  /* istanbul ignore next */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    this.loadingService.show(); // Show the spinner
    return this.userAuth.isAuthenticated$.pipe(
      switchMap((loggedIn) => {
        if (!loggedIn) {
          const screenHint = new URLSearchParams(window.location.search).get('screen_hint') ?? undefined;
          this.userAuth.login(state.url, screenHint);
          return of(false);
        }
        // User is authenticated; proceed to fetch user profile
        return this.fetchAndProcessUserProfile();
      }),
      finalize(() => this.loadingService.hide()) // Hide the spinner when the canActivate function completes
    );
  }

  private fetchAndProcessUserProfile(): Observable<boolean> {
    return this.userAuth.getTokenSilently$().pipe(
      switchMap(() => this.userAuth.getUserProfile$()),
      switchMap((userProfileData: UserProfileData) => {
        const userType =
          userProfileData['http://allegion.com/claims/user_type'];

        if (userType.includes('internal')) {
          // Log for internal users and skip further processing

          // Immediately return true to bypass additional API calls
          return of(true);
        } else {
          // For external users, proceed with additional profile data fetching
          // Check if userProfileData.sub exists to avoid unnecessary API call
          if (userProfileData && userProfileData.sub) {
            // Proceed with the profile data fetching
            return this.backend.getProfileData(userProfileData.sub).pipe(
              map((profileData: any) => {
                this.processUserProfileData(profileData);
                return true;
              }),
              catchError((error) => {
                console.error(
                  'Error fetching or processing user profile data:',
                  error
                );
                return of(true); // Optionally allow access despite the error
              })
            );
          } else {
            console.warn('No user profile data found.');
            return of(true);
          }
        }
      })
    );
  }

  private processUserProfileData(profileData: any): void {
    // Update the companyProfile$ BehaviorSubject with the fetched profile data
    this.companyProfile$.next(profileData);

    // Check if the profile data contains branchAssignments and it's an array
    if (Array.isArray(profileData.branchAssignments)) {
      // Check if there are multiple branch assignments
      const hasMultipleBranches = profileData.branchAssignments.length > 1;

      // Update the multipleBranches$ ReplaySubject based on the check
      this.multipleBranches$.next(hasMultipleBranches);

      // Update the branchAssignments$ BehaviorSubject with the branch assignments
      // If there are multiple branches, use the branchAssignments from profileData
      // Otherwise, set it to an empty array
      this.branchAssignments$.next(profileData.branchAssignments);
    } else {
      // If branchAssignments is not an array or doesn't exist,
      // Assume there are no multiple branches and no branch assignments
      this.multipleBranches$.next(false);
      this.branchAssignments$.next([]);
    }

    // Log for demonstration
  }

  /* istanbul ignore next */
  // checkIfUserHasMultipleRelevantRoles(roles: string[] = []): boolean {
  //   // console.log(roles, ' check multiple roles??');
  //   const relevantRoles = [
  //     'FullControl',
  //     'ViewOnly',
  //     'APIUser',
  //     'MobileCredUser',
  //   ];
  //   const userRoles = roles.filter((role) => relevantRoles.includes(role));
  //   return userRoles.length > 1;
  // }

  /* istanbul ignore next */
  // checkUserCompany(): Observable<boolean> {
  //   return this.userAuth.getUserProfile$().pipe(
  //     switchMap((results: UserProfileData) => {
  //       const checkForCompany = results.sub;
  //       return this.backend.getProfileData(checkForCompany).pipe(
  //         map((profileData: any) => {
  //           return profileData.companyAccountId !== '';
  //         })
  //       );
  //     })
  //   );
  // }
}
